import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubPage, Loading, Typo, ListTable, Filter, Flex } from '../../../ui';
import { useTasks } from '../../tasks/hooks/useTasksState';
import { TASK_TYPE } from '../../../utils/constants';
import { useCourseId } from '../../../hooks/readFromUrls';
import TaskResults from '../../tasks/components/TaskResults';
import { useNavigate, useParams } from 'react-router';
import PageControls from '../../../ui/Page/PageControls';

const CourseTasks = () => {
  const { t } = useTranslation();

  const { taskId, userId } = useParams();
  const navigate = useNavigate();

  const courseId = useCourseId();
  const { data: tasksData, isLoading, isFetching, isError, error } = useTasks({ courseId }, { skip: !courseId });
  // Stable empty array to avoid effect loops while data is undefined
  const EMPTY_ARRAY = React.useMemo(() => [], []);
  const tasks = tasksData?.tasks ?? EMPTY_ARRAY;
  const [localTasks, setLocalTasks] = useState(null);
  const [filter, setFilter] = useState('needsAttention');
  const [modalContent, setModalContent] = useState();
  const [savedCurrentPage, setSavedCurrentPage] = useState(0);

  const tasksFilters = useMemo(
    () => [
      { value: '0', label: t('tasks.notCorrectedResponses'), property: 'needsAttention' },
      { value: '1', label: t('tasks.allResponses'), property: '' },
    ],
    [t],
  );

  useEffect(() => {
    setModalContent(taskId ? String(taskId) : undefined);
  }, [taskId]);

  useEffect(() => {
    // Only update when upstream data or filter changes, avoid re-setting same state
    const next = filter ? tasks.filter((task) => task[filter]?.length > 0) : tasks;
    setLocalTasks((prev) => (prev === next ? prev : next));
  }, [tasks, filter]);

  const [tableInfo, setTableInfo] = useState([
    { label: t('globals.tasks'), data: (item) => item.question?.replace(/\[.*?\]/g, '').trim() },
    {
      label: t('globals.type'),
      hasSorting: { sortedMethod: 'number', propVal: 'type' },
      data: (item) =>
        item.type === TASK_TYPE.MULTIPLE
          ? t('tasks.singleOption')
          : item.type === TASK_TYPE.SINGLE
          ? t('tasks.multipleOption')
          : item.type === TASK_TYPE.OPEN
          ? t('tasks.openQuestion')
          : t(`tasks.taskType_${item.type}`),
    },
    { label: t('tasks.totalResponses'), data: (item) => item.usersCount },
    { label: t('tasks.incorrectResponses'), data: (item) => item.incorrect.length },
    {
      label: t('tasks.notCorrectedResponses'),
      hasSorting: { sortedMethod: 'length', propVal: 'needsAttention', initialOrder: 'asc' },
      data: (item) => item.needsAttention.length,
    },
  ]);

  const openDetails = (task) => {
    navigate(`/courses/${courseId}/tasks/${task.id}`);
  };

  const onCloseDetails = () => {
    navigate(`/courses/${courseId}/tasks`);
  };

  const handleSelectedFilter = useCallback(
    (value) => {
      const selectedFilter = tasksFilters.find((item) => item.value === value);
      setFilter(selectedFilter.property);
    },
    [tasksFilters],
  );

  const selectedFilter = useMemo(() => tasksFilters.find((item) => item.property === filter) || tasksFilters[0], [tasksFilters, filter]);

  const hasData = tasks.length > 0;
  const errorMessage = error?.data?.message || error?.data?.errMsg || error?.message || t('globals.genericError');
  const shouldShowLoadingSpinner = (isLoading || isFetching) && !hasData;

  if (shouldShowLoadingSpinner) {
    return (
      <SubPage align='flex-start'>
        <Loading />
      </SubPage>
    );
  }

  return (
    <SubPage align='flex-start'>
      {/* Background refetch indicator - subtle overlay that preserves content */}
      {isFetching && hasData && <Loading type='page' />}

      {isError && !hasData ? (
        <Typo.ContentSmallHeader>{errorMessage}</Typo.ContentSmallHeader>
      ) : !hasData ? (
        <Typo.ContentSmallHeader>{t('course.emptyTaskList')}</Typo.ContentSmallHeader>
      ) : (
        <Flex gap='40' direction='column' width='100%'>
          <PageControls openModelButton={<Filter info={tasksFilters} handleFilter={handleSelectedFilter} selected={selectedFilter.value} />} />
          {!!localTasks?.length ? (
            <ListTable
              tableInfo={tableInfo}
              data={localTasks}
              onRowClick={openDetails}
              tableName={'task-results'}
              tableSetter={setTableInfo}
              keepTrackOfPage={setSavedCurrentPage}
              defaultStartPage={savedCurrentPage}
            />
          ) : (
            <Typo.ContentSmallHeader>{t('tasks.emptyResults')}</Typo.ContentSmallHeader>
          )}
          <TaskResults userId={userId} taskId={modalContent} onClose={onCloseDetails} uncorrectedOnly={filter === 'needsAttention'} courseId={courseId} />
        </Flex>
      )}
    </SubPage>
  );
};

export default CourseTasks;
